import { type Merchant } from "types/merchant";
import { useSelector } from "react-redux";
import { useApi } from "hooks/useAPI";
import { useEffect, useState } from "react";
import { CostsService, Product } from "services/CostsService";
import { IVariant } from "types/variant";
import { Grid, Card, Stack } from "@mui/material";

import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Collapse,
  Box,
  Icon,
  Popover,
} from "@mui/material";
import { enqueueSnackbar, useSnackbar } from "notistack";
import {
  KeyboardArrowDown as KeyboardArrowDownIcon,
  KeyboardArrowUp as KeyboardArrowUpIcon,
  Edit as EditIcon,
  Save as SaveIcon,
  Close as CloseIcon,
  History as HistoryIcon,
} from "@mui/icons-material";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import VuiBox from "components/VuiBox";
import VuiTypography from "components/VuiTypography";
import breakpoints from "assets/theme/base/breakpoints";
import VuiInput from "components/VuiInput";
import VuiSelect from "components/VuiSelect";
import VuiButton from "components/VuiButton";
import VuiPagination from "components/VuiPagination";
import { IoSearchCircleOutline } from "react-icons/io5";
import AccessDeniedCard from "components/shared-components/AccessDeniedCard";
import { hasPrivilegedAccess } from "utils/roleAccess";
import { getApiErrorMessage } from "utils/apiError";

type SortField = "revenue" | "soldCount" | "lastSoldAt";
type SortOrder = "asc" | "desc";
const DEFAULT_PAGE_SIZE = 50;

interface RowProps {
  product: Product;
  onEdit: (variant: IVariant) => void;
  onSave: (id: string, ignoreInReporting: boolean) => void;
  onCancel: () => void;
  editingKey: string;
  selectedMerchant: any;
  formatCurrency: (value: number) => string;
}

const Row: React.FC<RowProps> = ({
  product,
  onEdit,
  onSave,
  onCancel,
  editingKey,
  selectedMerchant,
  formatCurrency,
}) => {
  const [open, setOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [ignoreInReporting, setIgnoreInReporting] = useState(false);
  const [historyAnchorEl, setHistoryAnchorEl] = useState<HTMLButtonElement | null>(null);
  const [selectedVariantHistory, setSelectedVariantHistory] = useState<any>(null);

  const handleHistoryClick = (event: React.MouseEvent<HTMLButtonElement>, variant: IVariant) => {
    setHistoryAnchorEl(event.currentTarget);
    setSelectedVariantHistory(variant);
  };

  const handleHistoryClose = () => {
    setHistoryAnchorEl(null);
    setSelectedVariantHistory(null);
  };

  const historyOpen = Boolean(historyAnchorEl);

  return (
    <>
      <TableRow>
        <TableCell style={{ minWidth: "38px" }}>
          <IconButton size="small" onClick={() => setOpen(!open)}>
            {open ? (
              <KeyboardArrowUpIcon sx={{ color: "white !important" }} />
            ) : (
              <KeyboardArrowDownIcon sx={{ color: "white !important" }} />
            )}
          </IconButton>
        </TableCell>
        <TableCell>
          <VuiTypography variant="button" color="white">
            {product.productTitle}
          </VuiTypography>
        </TableCell>
        <TableCell>
          <VuiTypography variant="button" color="white">
            {product.lastSoldAt
              ? new Date(product.lastSoldAt).toLocaleDateString()
              : "-"}
          </VuiTypography>
        </TableCell>
        <TableCell>
          <VuiTypography variant="button" color="white">
            {product.totalRevenue
              ? formatCurrency(product.totalRevenue)
              : formatCurrency(0)}
          </VuiTypography>
        </TableCell>
        <TableCell>
          <VuiTypography variant="button" color="white">
            {product.totalSold?.toLocaleString() || "0"}
          </VuiTypography>
        </TableCell>
      </TableRow>
      <TableRow>
        <TableCell colSpan={6} style={{ paddingBottom: 0, paddingTop: 0 }}>
          <Collapse in={open} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 1 }}>
              <Table sx={{ tableLayout: "fixed" }} size="small">
                <TableHead sx={{ display: "contents" }} >
                  <TableRow>
                    <TableCell style={{ minWidth: "280px" }}>
                      <VuiTypography variant="caption" color="text">
                        Variant Title
                      </VuiTypography>
                    </TableCell>
                    <TableCell style={{ minWidth: "280px" }}>
                      <VuiTypography variant="caption" color="text">
                        SKU
                      </VuiTypography>
                    </TableCell>
                    <TableCell style={{ minWidth: "260px" }}>
                      <VuiTypography variant="caption" color="text">
                        Cost Price
                      </VuiTypography>
                    </TableCell>
                    <TableCell style={{ minWidth: "300px" }}>
                      <VuiTypography variant="caption" color="text">
                        Cost History
                      </VuiTypography>
                    </TableCell>
                    <TableCell style={{ minWidth: "260px" }}>
                      <VuiTypography variant="caption" color="text">
                        Ignore In Reporting
                      </VuiTypography>
                    </TableCell>
                    <TableCell style={{ minWidth: "260px" }}>
                      <VuiTypography variant="caption" color="text">
                        Actions
                      </VuiTypography>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {product.variants.map((variant) => {
                    const isEditing = variant.id === editingKey;
                    return (
                      <TableRow key={variant.id}>
                        <TableCell sx={{ minWidth: "280px" }}>
                          <VuiTypography variant="button" color="white">
                            {variant.variantTitle}
                          </VuiTypography>
                        </TableCell>
                        <TableCell sx={{ minWidth: "280px" }}>
                          <VuiTypography variant="button" color="white">
                            {variant.sku}
                          </VuiTypography>
                        </TableCell>
                        <TableCell sx={{ minWidth: "100px" }}>
                          <VuiTypography variant="button" color="white">
                            {formatCurrency(variant.costPrice ?? 0)}
                          </VuiTypography>
                        </TableCell>
                        <TableCell sx={{ minWidth: "300px" }}>
                          {variant.cost_history && variant.cost_history.length > 1 ? (
                            <VuiBox display="flex" alignItems="center" gap={1}>
                              <VuiTypography variant="caption" color="text">
                                {variant.cost_history.length - 1} {variant.cost_history.length - 1 === 1 ? 'entry' : 'entries'}
                              </VuiTypography>
                              <IconButton
                                size="small"
                                onClick={(e) => handleHistoryClick(e, variant)}
                                sx={{ padding: '4px' }}
                              >
                                <HistoryIcon sx={{ color: "info.main", fontSize: "18px" }} />
                              </IconButton>
                            </VuiBox>
                          ) : (
                            <VuiTypography variant="caption" color="text">
                              No history
                            </VuiTypography>
                          )}
                        </TableCell>
                        <TableCell sx={{ minWidth: "100px" }}>
                          {isEditing ? (
                            <VuiSelect
                              //@ts-ignore
                              defaultValue={variant.ignoreInReporting}
                              onChange={(value: {
                                label: string;
                                value: string;
                              }) =>
                                setIgnoreInReporting(value.value === "true")
                              }
                              size="small"
                              options={[
                                { label: "Yes", value: "true" },
                                { label: "No", value: "false" },
                              ]}
                            />
                          ) : variant.ignoreInReporting ? (
                            <VuiTypography variant="button" color="white">
                              Yes
                            </VuiTypography>
                          ) : (
                            <VuiTypography variant="button" color="white">
                              No
                            </VuiTypography>
                          )}
                        </TableCell>
                        <TableCell sx={{ minWidth: "100px" }}>
                          {isEditing ? (
                            <>
                              <IconButton
                                size="small"
                                onClick={() =>
                                  onSave(
                                    variant.id,
                                    ignoreInReporting
                                  )
                                }
                              >
                                <SaveIcon sx={{ color: "white !important" }} />
                              </IconButton>
                              <IconButton size="small" onClick={onCancel}>
                                <CloseIcon sx={{ color: "white !important" }} />
                              </IconButton>
                            </>
                          ) : (
                            <IconButton
                              size="small"
                              onClick={() => onEdit(variant)}
                              disabled={editingKey !== ""}
                            >
                              <EditIcon sx={{ color: "white !important" }} />
                            </IconButton>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
      <Popover
        open={historyOpen}
        anchorEl={historyAnchorEl}
        onClose={handleHistoryClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            backgroundColor: '#0f1535',
            backgroundImage: 'linear-gradient(127.09deg, rgba(6, 11, 40, 0.94) 19.41%, rgba(10, 14, 35, 0.49) 76.65%)',
            border: '1px solid rgba(255, 255, 255, 0.125)',
          }
        }}
      >
        <VuiBox p={2} sx={{ minWidth: '280px', maxWidth: '400px' }}>
          <VuiTypography variant="h6" color="white" mb={2}>
            Cost History
          </VuiTypography>
          {selectedVariantHistory?.cost_history && selectedVariantHistory.cost_history.length > 1 ? (
            <VuiBox>
              {selectedVariantHistory.cost_history
                .sort((a: any, b: any) => new Date(b.effective_date).getTime() - new Date(a.effective_date).getTime())
                .slice(1)
                .map((history: any, index: number, array: any[]) => (
                  <VuiBox
                    key={index}
                    display="flex"
                    justifyContent="space-between"
                    alignItems="center"
                    py={1}
                    sx={{
                      borderBottom: index < array.length - 1 ? '1px solid rgba(255, 255, 255, 0.1)' : 'none'
                    }}
                  >
                    <VuiTypography variant="button" color="text">
                      {new Date(history.effective_date).toLocaleDateString()}
                    </VuiTypography>
                    <VuiTypography variant="button" color="white" fontWeight="bold">
                      {formatCurrency(history.price)}
                    </VuiTypography>
                  </VuiBox>
                ))}
            </VuiBox>
          ) : (
            <VuiTypography variant="caption" color="text">
              No history available
            </VuiTypography>
          )}
        </VuiBox>
      </Popover>
    </>
  );
};

const CostsAndExpenses: React.FC = () => {
  const { get, put, post } = useApi();
  const { values } = breakpoints;

  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(false);
  const [sortField, setSortField] = useState<SortField>("lastSoldAt");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [pagination, setPagination] = useState<any>({
    current: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    total: 0,
  });
  const [searchText, setSearchText] = useState<string>("");
  const [searchActive, setSearchActive] = useState<boolean>(false);
  const [hiddenFilter, setHiddenFilter] = useState<boolean | undefined>(
    undefined
  );
  const [updateAllHidden, setUpdateAllHidden] = useState(false);
  const [editingKey, setEditingKey] = useState("");

  const selectedMerchant: Merchant = useSelector(
    (state: any) => state.merchants.selectedMerchant
  );

  const userRole = useSelector((state: any) => state.merchants.selectedMerchant.roles?.name);
  const hasAccess = hasPrivilegedAccess(userRole);

  const formatCurrency = (value: number): string => {
    return value.toLocaleString("en-US", {
      style: "currency",
      currency: selectedMerchant?.currency || "USD",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const fetchVariants = async (
    page = 1,
    pageSize = DEFAULT_PAGE_SIZE,
    search: string,
    hidden: boolean | undefined
  ) => {
    if (!hasAccess) return;
    if (selectedMerchant) {
      setLoading(true);
      const costsService = new CostsService(get);
      try {
        const result = await costsService.getVariants({
          merchantId: selectedMerchant.id,
          page,
          limit: pageSize,
          search,
          hidden,
          sortBy: sortField,
          sortOrder,
        });
        setProducts(result.results);
        setPagination({
          ...pagination,
          current: page,
          pageSize,
          total: result.total,
        });
      } catch (error) {
        console.error("Error fetching variants:", error);
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchVariants(
      pagination.current,
      pagination.pageSize,
      searchText,
      hiddenFilter
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMerchant]);

  if (!hasAccess) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <VuiBox py={3}>
          <AccessDeniedCard
            title="Access Denied"
            message="Only owners can access and manage costs and expenses of selected merchant. Please contact your administrator for access."
          />
        </VuiBox>
      </DashboardLayout>
    );
  }

  const edit = (record: IVariant) => {
    setEditingKey(record.id);
  };

  const cancel = () => {
    setEditingKey("");
  };

  const save = async (
    key: string,
    ignoreInReporting: boolean
  ) => {
    console.log("save", key);
    try {
      // Update the variant in the API
      const costsService = new CostsService(put);
      await costsService.updateVariant(key, selectedMerchant.id, {
        ignoreInReporting,
      });

      // Reset editing state
      setEditingKey("");

      // Reload the current page of data
      fetchVariants(
        pagination.current,
        pagination.pageSize,
        searchText,
        hiddenFilter
      );

      enqueueSnackbar("Variant updated", {
        variant: "success",
        autoHideDuration: 3000,
      });
    } catch (errInfo) {
      console.log("Validate Failed:", errInfo);
      enqueueSnackbar(getApiErrorMessage(errInfo, "Failed to update variant"), {
        variant: "error",
      });
    }
  };


  const handleUpdateAllHidden = async () => {
    if (selectedMerchant) {
      const costsService = new CostsService(post);
      try {
        await costsService.setHiddenWithSearch(
          selectedMerchant.id,
          searchText,
          updateAllHidden
        );
        fetchVariants(
          pagination.current,
          pagination.pageSize,
          searchText,
          hiddenFilter
        );
        enqueueSnackbar("Reporting ignore for filtered variants updated", {
          variant: "success",
          autoHideDuration: 3000,
        });
      } catch (error) {
        console.error("Error updating all costs:", error);
        enqueueSnackbar(
          getApiErrorMessage(error, "Failed to update reporting ignore"),
          { variant: "error" }
        );
      }
    }
  };

  const handleTableChange = (newPagination: any, _: any, sorter: any) => {
    // Map table sort field to API sort field
    const sortFieldMap: {
      [key: string]: SortField;
    } = {
      totalRevenue: "revenue",
      totalSold: "soldCount",
      lastSoldAt: "lastSoldAt",
    };

    setSortField(sorter.field ? sortFieldMap[sorter.field] : "lastSoldAt");
    setSortOrder(
      sorter.order === "ascend"
        ? "asc"
        : sorter.order === "descend"
        ? "desc"
        : "desc"
    );

    fetchVariants(
      newPagination.current,
      newPagination.pageSize,
      searchText,
      hiddenFilter
    );
  };

  const handleSearch = () => {
    setSearchActive(!!searchText);
    fetchVariants(1, pagination.pageSize, searchText, hiddenFilter);
  };

  const handleHiddenFilterChange = (value: string) => {
    const hiddenValue =
      value === "true" ? true : value === "false" ? false : undefined;
    setHiddenFilter(hiddenValue);
    fetchVariants(1, pagination.pageSize, searchText, hiddenValue);
  };

  const pageSize = pagination.pageSize || DEFAULT_PAGE_SIZE;
  const currentPage = pagination.current || 1;
  const totalPages = Math.ceil((pagination.total || 0) / pageSize);
  const entriesStart = (currentPage - 1) * pageSize + 1;
  const entriesEnd = Math.min(currentPage * pageSize, pagination.total || 0);

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <VuiBox py={3}>
        <Grid container spacing={2}>
          <Grid item xs={24}>
            <VuiBox mb={3} p={1}>
              <VuiTypography
                variant={window.innerWidth < values.sm ? "h3" : "h2"}
                textTransform="capitalize"
                fontWeight="bold"
                color="white"
              >
                {loading && `Loading Variants`}
                {!loading && products.length === 0 && `No variants found`}
                {!loading &&
                  products.length > 0 &&
                  `${pagination.total?.toLocaleString()} variants`}
              </VuiTypography>
            </VuiBox>
          </Grid>
          <Grid item xs={24}>
            <Card>
              <VuiBox p={3}>
                <Grid container spacing={3} alignItems="center">
                  <Grid item xs={12} md={4}>
                    <VuiBox>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <VuiTypography
                          variant="caption"
                          color="text"
                          opacity={1}
                          textTransform="capitalize"
                          fontWeight="bold"
                        >
                          Search:
                        </VuiTypography>
                        <VuiInput
                          //@ts-ignore
                          placeholder="SKU, product or variant name"
                          value={searchText}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            setSearchText(e.target.value)
                          }
                          onPressEnter={handleSearch}
                          fullWidth
                          prefix={<IoSearchCircleOutline />}
                        />
                      </Stack>
                    </VuiBox>
                  </Grid>

                  <Grid item xs={12} md={4}>
                    <VuiBox>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <VuiTypography
                          variant="caption"
                          color="text"
                          opacity={1}
                          textTransform="capitalize"
                          fontWeight="bold"
                          mb={1}
                        >
                          Ignore In Reporting:
                        </VuiTypography>
                        <VuiSelect
                          //@ts-ignore
                          defaultValue={{ label: "All", value: "undefined" }}
                          options={[
                            { label: "All", value: "undefined" },
                            { label: "Hidden", value: "true" },
                            { label: "Visible", value: "false" },
                          ]}
                          onChange={(value: { label: string; value: string }) =>
                            handleHiddenFilterChange(value.value)
                          }
                          fullWidth
                        />
                      </Stack>
                    </VuiBox>
                  </Grid>
                </Grid>

                {searchActive && (pagination.total ?? 0) > 1 && (
                  <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid item xs={12}>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <VuiTypography
                          variant="caption"
                          color="text"
                          opacity={1}
                          textTransform="capitalize"
                          fontWeight="bold"
                          sx={{ minWidth: "147px" }}
                        >
                          Ignore all in reporting:
                        </VuiTypography>
                        <VuiSelect
                          //@ts-ignore
                          defaultValue={{ label: "All", value: "undefined" }}
                          options={[
                            { label: "Yes", value: "true" },
                            { label: "No", value: "false" },
                          ]}
                          onChange={(value: { label: string; value: string }) =>
                            setUpdateAllHidden(value.value === "true")
                          }
                          sx={{ flex: 1, maxWidth: "300px" }}
                        />
                        {/* @ts-ignore */}
                        <VuiButton onClick={handleUpdateAllHidden}>
                          Update All
                        </VuiButton>
                      </Stack>
                    </Grid>
                  </Grid>
                )}

                <TableContainer component={Paper}>
                  <Table sx={{ tableLayout: "fixed" }}>
                    <TableHead sx={{ display: "contents" }}>
                      <TableRow>
                        <TableCell style={{ minWidth: "38px" }} />
                        <TableCell style={{ minWidth: "543px" }}>
                          <VuiTypography variant="caption" color="text">
                            Product Title
                          </VuiTypography>
                        </TableCell>
                        <TableCell style={{ minWidth: "277px" }}>
                          <VuiTypography variant="caption" color="text">
                            Last Sold At
                          </VuiTypography>
                        </TableCell>
                        <TableCell style={{ minWidth: "277px" }}>
                          <VuiTypography variant="caption" color="text">
                            Total Revenue
                          </VuiTypography>
                        </TableCell>
                        <TableCell style={{ minWidth: "277px" }}>
                          <VuiTypography variant="caption" color="text">
                            Total Sold
                          </VuiTypography>
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {products.map((product) => (
                        <Row
                          key={product.productTitle}
                          product={product}
                          onEdit={edit}
                          onSave={save}
                          onCancel={cancel}
                          editingKey={editingKey}
                          selectedMerchant={selectedMerchant}
                          formatCurrency={formatCurrency}
                        />
                      ))}
                    </TableBody>
                  </Table>

                  <VuiBox
                    display="flex"
                    flexDirection={{ xs: "column", md: "row" }}
                    justifyContent="space-between"
                    alignItems={{ xs: "flex-start", md: "center" }}
                    p={3}
                  >
                    <VuiBox mb={{ xs: 3, md: 0 }}>
                      <VuiTypography
                        variant="button"
                        color="white"
                        fontWeight="regular"
                      >
                        Showing {entriesStart} to {entriesEnd} of{" "}
                        {pagination.total} entries
                      </VuiTypography>
                    </VuiBox>
                    {totalPages > 1 && (
                      //@ts-ignore
                      <VuiPagination variant="gradient" color="info">
                        {currentPage > 1 && (
                          //@ts-ignore
                          <VuiPagination
                            item
                            onClick={() =>
                              handleTableChange(
                                { ...pagination, current: currentPage - 1 },
                                null,
                                {}
                              )
                            }
                          >
                            <Icon sx={{ fontWeight: "bold" }}>
                              chevron_left
                            </Icon>
                          </VuiPagination>
                        )}

                        {totalPages <= 6 ? (
                          [...Array(totalPages)].map((_, index) => (
                            //@ts-ignore
                            <VuiPagination
                              key={index + 1}
                              item
                              active={currentPage === index + 1}
                              onClick={() =>
                                handleTableChange(
                                  { ...pagination, current: index + 1 },
                                  null,
                                  {}
                                )
                              }
                            >
                              {index + 1}
                            </VuiPagination>
                          ))
                        ) : (
                          <VuiBox width="5rem" mx={1}>
                            <VuiInput
                              //@ts-ignore
                              inputProps={{
                                type: "number",
                                min: 1,
                                max: totalPages,
                              }}
                              value={currentPage}
                              onChange={(
                                e: React.ChangeEvent<HTMLInputElement>
                              ) => {
                                const page = parseInt(e.target.value);
                                if (page >= 1 && page <= totalPages) {
                                  handleTableChange(
                                    { ...pagination, current: page },
                                    null,
                                    {}
                                  );
                                }
                              }}
                            />
                          </VuiBox>
                        )}

                        {currentPage < totalPages && (
                          //@ts-ignore
                          <VuiPagination
                            item
                            onClick={() =>
                              handleTableChange(
                                { ...pagination, current: currentPage + 1 },
                                null,
                                {}
                              )
                            }
                          >
                            <Icon sx={{ fontWeight: "bold" }}>
                              chevron_right
                            </Icon>
                          </VuiPagination>
                        )}
                      </VuiPagination>
                    )}
                  </VuiBox>
                </TableContainer>
              </VuiBox>
            </Card>
          </Grid>
        </Grid>
      </VuiBox>
    </DashboardLayout>
  );
};

export default CostsAndExpenses;
